import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";

import { LedgerReportData } from "../../types";
import { fetchLedgerReportData } from "../../utils/downloadReport";

interface LedgerReportSummaryProps {
  startDate: string;
  endDate: string;
  period: string;
  currency: string;
  onDownloadReport: (format: "pdf" | "csv") => void;
  downloadingFormat: "pdf" | "csv" | null;
}

export const LedgerReportSummary: React.FC<LedgerReportSummaryProps> = ({
  startDate,
  endDate,
  period,
  currency,
  onDownloadReport,
  downloadingFormat,
}) => {
  const [report, setReport] = useState<LedgerReportData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchLedgerReportData({
        startDate,
        endDate,
        period,
      });

      setReport(data);
    } catch (err: any) {
      setError(err?.message || "Failed to load ledger report summary.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [startDate, endDate, period]);

  const formatCurrency = (val: number) =>
    `${currency} ${Number(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const getStatusBadgeStyle = (status: string) => {
    const s = status.toLowerCase();

    if (s.includes("fulfilled") && !s.includes("partially")) {
      return { bg: "#e8f5e9", color: "#2e7d32" };
    }

    if (s.includes("partially")) {
      return { bg: "#fff3e0", color: "#ef6c00" };
    }

    if (s.includes("unfulfilled")) {
      return { bg: "#fffde7", color: "#f57f17" };
    }

    if (s.includes("canceled")) {
      return { bg: "#ffebee", color: "#c62828" };
    }

    if (s.includes("paid")) {
      return { bg: "#e8f5e9", color: "#1b5e20" };
    }

    if (s.includes("pending")) {
      return { bg: "#fff3e0", color: "#e65100" };
    }

    return { bg: "#eceff1", color: "#546e7a" };
  };

  if (loading && !report) {
    return (
      <Box
        padding={10}
        display="flex"
        justifyContent="center"
        alignItems="center"
        backgroundColor="default1"
        borderRadius={3}
      >
        <Text size={4} color="default1">
          Generating live sales & financial ledger report summary...
        </Text>
      </Box>
    );
  }

  if (error && !report) {
    return (
      <Box
        padding={6}
        backgroundColor="default2"
        borderRadius={3}
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Text size={4} fontWeight="bold" color="critical1">
          Unable to Load Report Summary
        </Text>
        <Text size={2} color="default1">
          {error}
        </Text>
        <Button variant="primary" size="small" onClick={loadReport}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!report) {
    return null;
  }

  const kpis = [
    {
      title: "Total Orders",
      value: report.total_orders_count.toLocaleString("en-IN"),
      sub: `${formatCurrency(report.total_orders_amount)} gross`,
      color: "default1",
    },
    {
      title: "Completed Sales",
      value: formatCurrency(report.completed_sales_amount),
      sub: `${report.completed_sales_count} fulfilled & paid`,
      color: "default1",
    },
    {
      title: "Total Expenses",
      value: formatCurrency(report.total_expenses_amount),
      sub: `${report.total_expenses_count} entries recorded`,
      color: "default1",
    },
    {
      title: "Net Profit",
      value: formatCurrency(report.profit),
      sub: "Sales - Expenses",
      textColor: report.profit >= 0 ? "#2e7d32" : "#c62828",
    },
    {
      title: "Pending Payments",
      value: formatCurrency(report.pending_payments_amount),
      sub: `${report.pending_payments_count} orders pending`,
      textColor: "#e65100",
    },
    {
      title: "Expected Net Total",
      value: formatCurrency(report.net_total),
      sub: "Sales + Pending - Exp",
      textColor: "#1565c0",
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={6}>
      {/* 1. Executive Report Header Card */}
      <Box
        padding={5}
        borderRadius={3}
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        backgroundColor="default1"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={4}
        style={{ borderLeft: "5px solid #793923" }}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" alignItems="center" gap={3}>
            <Text
              size={5}
              fontWeight="bold"
              style={{ color: "#793923", textTransform: "uppercase" }}
            >
              Kisan Basket
            </Text>
            <Text size={4} fontWeight="bold" color="default1">
              • Sales & Financial Ledger Report
            </Text>
            <span
              style={{
                backgroundColor: "#793923",
                color: "#ffffff",
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "10px",
              }}
            >
              {report.period_label}
            </span>
          </Box>
          <Text size={2} color="default1">
            Date Range: <strong>{report.start_date}</strong> to <strong>{report.end_date}</strong> •
            Generated: {report.generated_at}
          </Text>
        </Box>

        {/* Action Controls */}
        <Box display="flex" alignItems="center" gap={2}>
          <Button variant="secondary" size="small" disabled={loading} onClick={loadReport}>
            {loading ? "Refreshing..." : "Refresh Summary"}
          </Button>
          <Button
            variant="secondary"
            size="small"
            disabled={!!downloadingFormat}
            onClick={() => onDownloadReport("pdf")}
          >
            {downloadingFormat === "pdf" ? "Generating PDF..." : "Download PDF"}
          </Button>
          <Button
            variant="secondary"
            size="small"
            disabled={!!downloadingFormat}
            onClick={() => onDownloadReport("csv")}
          >
            {downloadingFormat === "csv" ? "Exporting CSV..." : "Download CSV"}
          </Button>
        </Box>
      </Box>

      {/* 2. Key Financial KPI Cards */}
      <Box display="grid" __gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))" gap={3}>
        {kpis.map((k, idx) => (
          <Box
            key={idx}
            padding={4}
            borderRadius={3}
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            backgroundColor="default1"
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Text
              size={1}
              fontWeight="bold"
              color="default1"
              style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
            >
              {k.title}
            </Text>
            <Text size={5} fontWeight="bold" style={{ color: k.textColor || "inherit" }}>
              {k.value}
            </Text>
            <Text size={1} color="default1">
              {k.sub}
            </Text>
          </Box>
        ))}
      </Box>

      {/* 3. Side-by-Side Summary Tables */}
      <Box display="grid" __gridTemplateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={4}>
        {/* Order Status Breakdown */}
        <Box
          padding={4}
          borderRadius={3}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          backgroundColor="default1"
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Text size={4} fontWeight="bold">
            Order Status Breakdown
          </Text>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ backgroundColor: "#fafafa", borderBottom: "1px solid #eee" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Status</th>
                  <th style={{ padding: "8px 10px", textAlign: "center" }}>Orders</th>
                  <th style={{ padding: "8px 10px", textAlign: "right" }}>Gross Amount</th>
                </tr>
              </thead>
              <tbody>
                {report.order_status_breakdown.map((item, idx) => {
                  const badge = getStatusBadgeStyle(item.status);

                  return (
                    <tr key={idx} style={{ borderBottom: "1px solid #f5f5f5" }}>
                      <td style={{ padding: "8px 10px" }}>
                        <span
                          style={{
                            backgroundColor: badge.bg,
                            color: badge.color,
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontSize: "11px",
                            fontWeight: 700,
                          }}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: "bold" }}>
                        {item.count}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right" }}>
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  );
                })}
                <tr
                  style={{
                    backgroundColor: "#faf8f7",
                    fontWeight: "bold",
                    borderTop: "2px solid #e0e0e0",
                  }}
                >
                  <td style={{ padding: "10px" }}>TOTAL ORDERS</td>
                  <td style={{ padding: "10px", textAlign: "center" }}>
                    {report.total_orders_count}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right" }}>
                    {formatCurrency(report.total_orders_amount)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Box>

        {/* Payment Status Breakdown & Period Financial Summary */}
        <Box
          padding={4}
          borderRadius={3}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          backgroundColor="default1"
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Text size={4} fontWeight="bold">
            Payment Status & Net Reconciliation
          </Text>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ backgroundColor: "#fafafa", borderBottom: "1px solid #eee" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Payment Category</th>
                  <th style={{ padding: "8px 10px", textAlign: "center" }}>Orders</th>
                  <th style={{ padding: "8px 10px", textAlign: "right" }}>Gross Amount</th>
                </tr>
              </thead>
              <tbody>
                {report.payment_status_breakdown.map((item, idx) => {
                  const badge = getStatusBadgeStyle(item.status);

                  return (
                    <tr key={idx} style={{ borderBottom: "1px solid #f5f5f5" }}>
                      <td style={{ padding: "8px 10px" }}>
                        <span
                          style={{
                            backgroundColor: badge.bg,
                            color: badge.color,
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontSize: "11px",
                            fontWeight: 700,
                          }}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: "bold" }}>
                        {item.count}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: "bold" }}>
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Text size={3} fontWeight="bold" marginTop={2}>
            Period Financial Summary
          </Text>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <tbody>
                <tr style={{ borderBottom: "1px solid #f5f5f5" }}>
                  <td style={{ padding: "8px 10px" }}>
                    <strong>Gross Realized Revenue</strong> (Fulfilled & Paid)
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      textAlign: "right",
                      color: "#2e7d32",
                      fontWeight: "bold",
                    }}
                  >
                    {formatCurrency(report.completed_sales_amount)}
                  </td>
                </tr>
                <tr style={{ borderBottom: "1px solid #f5f5f5" }}>
                  <td style={{ padding: "8px 10px" }}>
                    <strong>Total Operational Expenses</strong>
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      textAlign: "right",
                      color: "#c62828",
                      fontWeight: "bold",
                    }}
                  >
                    -{formatCurrency(report.total_expenses_amount)}
                  </td>
                </tr>
                <tr style={{ backgroundColor: "#faf8f7", borderTop: "2px solid #e0e0e0" }}>
                  <td style={{ padding: "10px", fontWeight: "bold" }}>Net Operating Profit</td>
                  <td
                    style={{
                      padding: "10px",
                      textAlign: "right",
                      fontWeight: "bold",
                      color: report.profit >= 0 ? "#2e7d32" : "#c62828",
                    }}
                  >
                    {formatCurrency(report.profit)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Box>
      </Box>

      {/* 4. Day-by-Day Financial Breakdown Table */}
      {report.daily_breakdown && report.daily_breakdown.length > 0 && (
        <Box
          padding={4}
          borderRadius={3}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          backgroundColor="default1"
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Text size={4} fontWeight="bold">
              Day-by-Day Financial & Order Breakdown
            </Text>
            <Text size={2} color="default1">
              {report.daily_breakdown.length} Active Days
            </Text>
          </Box>

          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "12px",
                textAlign: "left",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#efebe9", borderBottom: "1px solid #d7ccc8" }}>
                  <th style={{ padding: "8px" }}>Date</th>
                  <th style={{ padding: "8px", textAlign: "center" }}>Orders</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>Gross</th>
                  <th style={{ padding: "8px", textAlign: "center" }}>Completed</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>Sales</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>Expenses</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>Profit</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>Pending</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>Net Total</th>
                  <th style={{ padding: "8px", textAlign: "center" }}>Fulfilled</th>
                  <th style={{ padding: "8px", textAlign: "center" }}>Canceled</th>
                  <th style={{ padding: "8px", textAlign: "center" }}>Paid</th>
                </tr>
              </thead>
              <tbody>
                {report.daily_breakdown.map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "8px", fontWeight: "bold" }}>{row.display_date}</td>
                    <td style={{ padding: "8px", textAlign: "center" }}>
                      {row.total_orders_count}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {formatCurrency(row.total_orders_amount)}
                    </td>
                    <td style={{ padding: "8px", textAlign: "center" }}>
                      {row.completed_sales_count}
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        color: "#2e7d32",
                        fontWeight: "bold",
                      }}
                    >
                      {formatCurrency(row.completed_sales_amount)}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right", color: "#c62828" }}>
                      {formatCurrency(row.expenses_amount)}
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        fontWeight: "bold",
                        color: row.profit >= 0 ? "#2e7d32" : "#c62828",
                      }}
                    >
                      {formatCurrency(row.profit)}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {formatCurrency(row.pending_payments_amount)}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right", fontWeight: "bold" }}>
                      {formatCurrency(row.net_total)}
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", color: "#2e7d32" }}>
                      {row.fulfilled_count}
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", color: "#c62828" }}>
                      {row.canceled_count}
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", color: "#1b5e20" }}>
                      {row.fully_paid_count}
                    </td>
                  </tr>
                ))}
                <tr
                  style={{
                    backgroundColor: "#efebe9",
                    fontWeight: "bold",
                    borderTop: "2px solid #a1887f",
                  }}
                >
                  <td style={{ padding: "10px" }}>PERIOD TOTAL</td>
                  <td style={{ padding: "10px", textAlign: "center" }}>
                    {report.total_orders_count}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right" }}>
                    {formatCurrency(report.total_orders_amount)}
                  </td>
                  <td style={{ padding: "10px", textAlign: "center" }}>
                    {report.completed_sales_count}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", color: "#2e7d32" }}>
                    {formatCurrency(report.completed_sales_amount)}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", color: "#c62828" }}>
                    {formatCurrency(report.total_expenses_amount)}
                  </td>
                  <td
                    style={{
                      padding: "10px",
                      textAlign: "right",
                      color: report.profit >= 0 ? "#2e7d32" : "#c62828",
                    }}
                  >
                    {formatCurrency(report.profit)}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right" }}>
                    {formatCurrency(report.pending_payments_amount)}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right" }}>
                    {formatCurrency(report.net_total)}
                  </td>
                  <td style={{ padding: "10px", textAlign: "center" }}>
                    {report.order_status_breakdown[0]?.count || 0}
                  </td>
                  <td style={{ padding: "10px", textAlign: "center" }}>
                    {report.order_status_breakdown[5]?.count || 0}
                  </td>
                  <td style={{ padding: "10px", textAlign: "center" }}>
                    {report.payment_status_breakdown[0]?.count || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Box>
      )}

      {/* 5. Expenses by Category Table */}
      {report.expense_categories && report.expense_categories.length > 0 && (
        <Box
          padding={4}
          borderRadius={3}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          backgroundColor="default1"
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Text size={4} fontWeight="bold">
            Operational Expenses by Category ({report.total_expenses_count} entries recorded)
          </Text>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ backgroundColor: "#fafafa", borderBottom: "1px solid #eee" }}>
                  <th style={{ padding: "8px 10px", width: "50px", textAlign: "center" }}>#</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Category</th>
                  <th style={{ padding: "8px 10px", textAlign: "center" }}>Entries Count</th>
                  <th style={{ padding: "8px 10px", textAlign: "right" }}>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {report.expense_categories.map((cat, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <td style={{ padding: "8px 10px", textAlign: "center", color: "#888" }}>
                      {idx + 1}
                    </td>
                    <td style={{ padding: "8px 10px", fontWeight: "bold" }}>
                      {cat.category_label || cat.category}
                    </td>
                    <td style={{ padding: "8px 10px", textAlign: "center" }}>{cat.count}</td>
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "right",
                        fontWeight: "bold",
                        color: "#c62828",
                      }}
                    >
                      {formatCurrency(cat.amount)}
                    </td>
                  </tr>
                ))}
                <tr
                  style={{
                    backgroundColor: "#faf8f7",
                    fontWeight: "bold",
                    borderTop: "2px solid #e0e0e0",
                  }}
                >
                  <td colSpan={2} style={{ padding: "10px" }}>
                    TOTAL EXPENSES
                  </td>
                  <td style={{ padding: "10px", textAlign: "center" }}>
                    {report.total_expenses_count}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", color: "#c62828" }}>
                    {formatCurrency(report.total_expenses_amount)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Box>
      )}

      {/* 6. Detailed Individual Expense Entries (if available) */}
      {report.individual_expenses && report.individual_expenses.length > 0 && (
        <Box
          padding={4}
          borderRadius={3}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          backgroundColor="default1"
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Text size={4} fontWeight="bold">
            Detailed Individual Expense Entries for {report.period_label}
          </Text>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ backgroundColor: "#fafafa", borderBottom: "1px solid #eee" }}>
                  <th style={{ padding: "8px 10px", width: "70px" }}>ID</th>
                  <th style={{ padding: "8px 10px" }}>Category</th>
                  <th style={{ padding: "8px 10px" }}>Description</th>
                  <th style={{ padding: "8px 10px", textAlign: "right" }}>Amount</th>
                  <th style={{ padding: "8px 10px", textAlign: "center" }}>Logged Time</th>
                </tr>
              </thead>
              <tbody>
                {report.individual_expenses.map((exp, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <td style={{ padding: "8px 10px", color: "#888" }}>#{exp.id}</td>
                    <td style={{ padding: "8px 10px", fontWeight: "bold" }}>
                      {exp.category_label || exp.category}
                    </td>
                    <td style={{ padding: "8px 10px" }}>{exp.description || "—"}</td>
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "right",
                        fontWeight: "bold",
                        color: "#c62828",
                      }}
                    >
                      {formatCurrency(exp.amount)}
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "center",
                        color: "#777",
                        fontSize: "12px",
                      }}
                    >
                      {exp.created_at || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Box>
      )}
    </Box>
  );
};
